import React, { ReactElement, memo } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import ListEmpty from "@/features/invitations/components/molecules/ListEmpty";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import SkeletonList from "@/components/organisms/SkeletonList";
import { invitationsListContentContainerStyle } from "@/features/invitations/constants";
import { InvitationsInvitationFragment } from "@/generated/graphql";

/**
 * Props for the MainView component.
 */
interface Props {
  invitations: InvitationsInvitationFragment[];
  renderItem: ListRenderItem<InvitationsInvitationFragment>;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderListFooterComponent: () => ReactElement | null;
  loading: boolean;
  refreshing: boolean;
  widthContent: number;
}
const keyExtractor = (_: InvitationsInvitationFragment, index: number) =>
  index.toString();

/**
 * View component for displaying the main invitations list with pagination and pull-to-refresh.
 * @param root0 - The component props
 * @param root0.invitations - Array of invitation data fragments to display in the list
 * @param root0.renderItem - Function to render each individual invitation item in the list
 * @param root0.loading - Boolean indicating if initial invitation data is being loaded
 * @param root0.refreshing - Boolean indicating if pull-to-refresh operation is in progress
 * @param root0.widthContent - The content width for responsive layout sizing
 * @param root0.onLoadMore - Callback function to load more invitations when scrolling reaches the end
 * @param root0.onRefresh - Callback function to refresh the invitation list
 * @param root0.renderListFooterComponent - Function to render custom footer component for pagination or loading states
 */
const MainView = ({
  invitations,
  renderItem,
  loading,
  refreshing,
  widthContent,
  onLoadMore,
  onRefresh,
  renderListFooterComponent,
}: Props) => (
  <FlashList
    scrollEventThrottle={16}
    refreshing={refreshing}
    contentContainerStyle={invitationsListContentContainerStyle(widthContent)}
    data={invitations}
    keyExtractor={keyExtractor}
    renderItem={renderItem}
    ListEmptyComponent={
      loading ? (
        <SkeletonList type="manage-invitations" />
      ) : (
        <ListEmpty
          title="No Invitations"
          description="No pending invitations."
        />
      )
    }
    ListFooterComponent={renderListFooterComponent}
    onEndReached={onLoadMore}
    onRefresh={onRefresh}
    refreshControl={
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        colors={["white"]}
        tintColor="white"
      />
    }
    ItemSeparatorComponent={ItemSeparator}
  />
);

MainView.displayName = "MainView";

export default memo(MainView);
